/**
 * Französische ID-Generator - ISO-Standard Konform
 * Generiert sequenzielle IDs nach ISO-Standards für ERP-Systeme
 * 
 * Migration von zufälligen zu sequenziellen Nummern
 */

// Import centralized Business ID function
import { getCurrentBusinessId } from './sharedCore';
import { kvGet } from '../infrastructure/storage/kvAccess';
import isoStandardIdGenerator from './ISOStandardIdGenerator';
import { generateProductId as generateProductIdFromUtils } from './productIdUtils';
import { formatSequenceDigits } from './sequenceDigits';

export { generateProductId, canonicalProductId } from './productIdUtils';

// Importiere Tracking-Services (lazy loading to avoid circular dependency)
let idTracker = null;
let idPoolManager = null;

// Lazy load services to avoid circular dependency
const getIDTracker = () => {
  if (!idTracker) {
    try {
      idTracker = require('../services/IDTracker').default;
    } catch (error) {
      console.warn('IDTracker not available:', error);
      return null;
    }
  }
  return idTracker;
};

const getIDPoolManager = () => {
  if (!idPoolManager) {
    idPoolManager = require('../services/IDPoolManager').default;
  }
  return idPoolManager;
};

export function syncBusinessSequences(businessId = null) {
  try {
    isoStandardIdGenerator.syncSequencesAfterPersist(
      businessId || getCurrentBusinessId({ allowFallback: false })
    );
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[frenchIdGenerator] syncBusinessSequences skipped:', err?.message || err);
    }
  }
}

/** Temporärer Lieferanten-Schlüssel für Einkaufsentwürfe (keine FOUR-Nummer verbrennen). */
const DRAFT_SUPPLIER_PREFIX = '__draft_supplier__:';

export function draftSupplierKey(name) {
  return `${DRAFT_SUPPLIER_PREFIX}${String(name || '').trim()}`;
}

export function isDraftSupplierKey(id) {
  return String(id || '').startsWith(DRAFT_SUPPLIER_PREFIX);
}

export function supplierNameFromDraftKey(id) {
  return String(id || '').slice(DRAFT_SUPPLIER_PREFIX.length);
}

function allocateIsoId(type, prefix) {
  return isoStandardIdGenerator.allocateSequentialId(type, prefix);
}

/** Nächste VENTE-Nummer (Vorschau — spiegelt allocateSequentialId inkl. inflight). */
export async function peekVenteId(businessId = null) {
  void businessId;
  return isoStandardIdGenerator.peekAllocateSequentialId('VENTE', 'VENTE');
}

/** Nächste CLIENT-Nummer (Vorschau — spiegelt allocateSequentialId inkl. inflight). */
export function peekClientId() {
  return isoStandardIdGenerator.peekAllocateSequentialId('CLIENT', 'CLIENT');
}

/**
 * Generiert eine VENTE-ID (Verkauf) - ISO-Standard Konform
 * Format: VENTE-YYYY-NNNNNN (sequenziell)
 * Peek-only: Zähler steigt erst nach gespeichertem Verkauf (syncBusinessSequences).
 */
export const generateVenteId = async (businessId = null, options = {}) => {
  try {
    const isoId = allocateIsoId('VENTE', 'VENTE');
    
    // Tracking (Phase 4) - Check if tracker exists
    const tracker = getIDTracker();
    if (tracker && typeof tracker.trackId === 'function') {
      tracker.trackId(isoId, 'vente', {
        businessId: businessId || getCurrentBusinessId({ allowFallback: false }),
        userId: options.userId || 'unknown',
        source: options.source || 'frenchIdGenerator',
        generationTime: 0,
        retries: 0,
        method: 'iso_sequential'
      });
    }
    
    console.log('generateVenteId: Generated ISO-compliant VENTE ID:', isoId);
    return isoId;
    
  } catch (error) {
    console.error('generateVenteId: Error generating ISO VENTE ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `VENTE-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateVenteId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine ACHAT-ID (Einkauf) - ISO-Standard Konform
 * Format: ACHAT-YYYY-NNNNNN (sequenziell)
 */
export const generateAchatId = async (businessId = null) => {
  try {
    const isoId = allocateIsoId('ACHAT', 'ACHAT');
    return isoId;
    
  } catch (error) {
    console.error('generateAchatId: Error generating ISO ACHAT ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `ACHAT-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateAchatId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine CLIENT-ID (Kunde) - ISO-Standard Konform
 * Format: CLIENT-YYYY-NNNNNN (sequenziell)
 */
export const generateClientId = async (businessId = null) => {
  try {
    const isoId = allocateIsoId('CLIENT', 'CLIENT');
    return isoId;
    
  } catch (error) {
    console.error('generateClientId: Error generating ISO CLIENT ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `CLIENT-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateClientId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine FOURNISSEUR-ID (Lieferant) - ISO-Standard Konform
 * Format: FOUR-YYYY-NNNNNN (sequenziell)
 */
export const generateFournisseurId = (businessId = null) => {
  try {
    const isoId = allocateIsoId('FOURNISSEUR', 'FOUR');
    
    console.log('generateFournisseurId: Generated ISO-compliant FOURNISSEUR ID:', isoId);
    return isoId;
    
  } catch (error) {
    console.error('generateFournisseurId: Error generating ISO FOURNISSEUR ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `FOUR-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateFournisseurId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine COMMANDE-ID (Bestellung) - ISO-Standard Konform
 * Format: CMD-YYYY-NNNNNN (sequenziell)
 */
export const generateCommandeId = (businessId = null) => {
  try {
    const isoId = allocateIsoId('COMMANDE', 'CMD');
    
    console.log('generateCommandeId: Generated ISO-compliant COMMANDE ID:', isoId);
    return isoId;
    
  } catch (error) {
    console.error('generateCommandeId: Error generating ISO COMMANDE ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `CMD-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateCommandeId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine DEPENSE-ID (Ausgabe) - ISO-Standard Konform
 * Format: DEP-YYYY-NNNNNN (sequenziell)
 */
export const generateDepenseId = (businessId = null) => {
  try {
    const isoId = allocateIsoId('DEPENSE', 'DEP');
    console.log('generateDepenseId: Generated ISO-compliant DEPENSE ID:', isoId);
    return isoId;
  } catch (error) {
    console.error('generateDepenseId: Error generating ISO DEPENSE ID:', error);
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `DEP-EMERGENCY-${timestamp}${random}`;
    console.warn('generateDepenseId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Generiert eine TRAVAILLEUR-ID (Arbeiter) - ISO-Standard Konform
 * Format: TRAV-YYYY-NNNNNN (sequenziell)
 */
export const generateTravailleurId = (businessId = null, code = null) => {
  try {
    const isoId = allocateIsoId('TRAVAILLEUR', 'TRAV');
    
    console.log('generateTravailleurId: Generated ISO-compliant TRAVAILLEUR ID:', isoId);
    return isoId;
    
  } catch (error) {
    console.error('generateTravailleurId: Error generating ISO TRAVAILLEUR ID:', error);
    
    // Fallback: Timestamp-basierte ID
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const fallbackId = `TRAV-EMERGENCY-${timestamp}${random}`;
    
    console.warn('generateTravailleurId: Using emergency fallback ID:', fallbackId);
    return fallbackId;
  }
};

/**
 * Hilfsfunktionen
 */

// Duplikat-Prüfung
const checkIdExists = (id, type) => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) return false;
    
    const key = `${businessId}_${type}s`;
    const raw = kvGet(key);
    let data = [];
    if (Array.isArray(raw)) {
      data = raw;
    } else if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        data = Array.isArray(parsed) ? parsed : [];
      } catch {
        data = [];
      }
    }
    
    // Prüfe verschiedene ID-Felder
    return data.some(item => 
      item.id === id || 
      item[`${type}Id`] === id ||
      item.orderId === id ||
      item.purchaseId === id ||
      item.customerId === id ||
      item.supplierId === id
    );
  } catch (error) {
    console.error('Error checking ID existence:', error);
    return false;
  }
};

// Arbeiter-Code generieren (5-6 Ziffern)
const generateWorkerCode = () => {
  let attempts = 0;
  let newCode;
  
  do {
    newCode = Math.floor(10000 + Math.random() * 90000).toString();
    attempts++;
    
    if (attempts > 100) {
      newCode = Math.floor(100000 + Math.random() * 900000).toString();
    }
  } while (attempts < 200);
  
  return newCode;
};

/**
 * Validierung französischer IDs
 */
export const validateFrenchId = (id, type) => {
  const patterns = {
    vente: /^VENTE\d{5,6}$/,
    achat: /^ACHAT\d{5,6}$/,
    client: /^CLIENT\d{5,6}$/,
    fournisseur: /^FOUR\d{5,6}$/,
    commande: /^CMD\d{5,6}$/,
    depense: /^DEP\d{5,6}$/,
    travailleur: /^TRAV\d{5,6}$/
  };
  
  const pattern = patterns[type];
  if (!pattern) {
    return { valid: false, error: `Unknown French ID type: ${type}` };
  }
  
  const isValid = pattern.test(id);
  return {
    valid: isValid,
    error: isValid ? null : `Invalid French ${type.toUpperCase()} ID format`
  };
};

/**
 * Migration von alten IDs zu französischen IDs
 */
export const migrateToFrenchIds = (oldId, type) => {
  const businessId = getCurrentBusinessId();
  if (!businessId) {
    throw new Error('Business ID not found for migration');
  }
  
  // Kurze französische IDs
  const frenchIdMap = {
    'SAL': () => generateVenteId(businessId),
    'SALE': () => generateVenteId(businessId),
    'PUR': () => generateAchatId(businessId),
    'CUST': () => generateClientId(businessId),
    'SUP': () => generateFournisseurId(businessId),
    'ORD': () => generateCommandeId(businessId),
    'DEP': () => generateDepenseId(businessId),
    'W': () => generateTravailleurId(businessId, oldId.replace('W', ''))
  };
  
  // Finde passendes Pattern
  for (const [prefix, generator] of Object.entries(frenchIdMap)) {
    if (oldId.startsWith(prefix)) {
      return generator();
    }
  }
  
  // Fallback: Generiere neue französische ID
  switch (type) {
    case 'vente':
      return generateVenteId(businessId);
    case 'achat':
      return generateAchatId(businessId);
    case 'client':
      return generateClientId(businessId);
    case 'fournisseur':
      return generateFournisseurId(businessId);
    case 'commande':
      return generateCommandeId(businessId);
    case 'depense':
      return generateDepenseId(businessId);
    default:
      throw new Error(`Unknown type for migration: ${type}`);
  }
};

/**
 * Format product SKU sequence number.
 * 1–999 → 3 digits (001…999); 1000–9999 → 4 digits (1000…9999).
 * @param {number} n
 * @returns {string}
 */
export function formatProductSkuNumber(n) {
  return formatSequenceDigits(n);
}

/**
 * Normalize PROD### SKUs to canonical digit width; leave custom SKUs unchanged.
 * @param {unknown} sku
 * @returns {string}
 */
export function normalizeProductSku(sku) {
  const s = String(sku || '').trim();
  const m = s.match(/^PROD(\d+)$/i);
  if (!m) return s;
  return `PROD${formatProductSkuNumber(parseInt(m[1], 10))}`;
}

/**
 * Generate automatic SKU for products
 * Format: PROD001…PROD999 (3-stellig), dann PROD1000…PROD9999 (4-stellig)
 */
export const generateProductSKU = (products = []) => {
  if (!Array.isArray(products)) {
    return 'PROD001';
  }

  // Find the highest existing SKU number
  let maxNumber = 0;
  
  products.forEach(product => {
    if (product.sku && typeof product.sku === 'string') {
      const match = product.sku.match(/^PROD(\d+)$/i);
      if (match) {
        const number = parseInt(match[1], 10);
        if (number > maxNumber) {
          maxNumber = number;
        }
      }
    }
  });

  // Generate next SKU (3-stellig bis 999, danach 4-stellig bis 9999)
  const nextNumber = maxNumber + 1;
  return `PROD${formatProductSkuNumber(nextNumber)}`;
};

/**
 * Bei Create immer id + SKU am Katalogprodukt halten (SKU bleibt an productId gebunden).
 * @param {object} product
 * @param {Array} existingProducts
 * @param {{ offset?: number }} [options]
 * @returns {object}
 */
export function ensureCatalogProductIdentity(product = {}, existingProducts = [], { offset = 0, deferSku = false } = {}) {
  const list = Array.isArray(existingProducts) ? existingProducts : [];
  const hasId = product.id != null && product.id !== '';
  const id = hasId ? product.id : generateProductIdFromUtils(list, { offset });
  const explicitSku = String(product.sku || '').trim();
  let sku = explicitSku;
  if (!sku && !deferSku) {
    sku = generateProductSKU(list);
  }
  return { ...product, id, sku: sku || '' };
}

/**
 * Vergibt fehlende SKUs erst beim Batch-Speichern (Warteschlange / Import).
 */
export function assignMissingProductSkus(products, existingProducts = []) {
  const list = Array.isArray(existingProducts) ? existingProducts : [];
  const out = [];
  let pool = [...list];
  for (const product of Array.isArray(products) ? products : []) {
    const explicit = String(product?.sku || '').trim();
    if (explicit) {
      const row = { ...product, sku: explicit };
      out.push(row);
      pool.push(row);
      continue;
    }
    const sku = generateProductSKU(pool);
    const row = { ...product, sku };
    out.push(row);
    pool.push(row);
  }
  return out;
}

export default {
  generateVenteId,
  generateAchatId,
  generateClientId,
  generateFournisseurId,
  generateCommandeId,
  generateDepenseId,
  generateTravailleurId,
  generateProductSKU,
  generateProductId: generateProductIdFromUtils,
  ensureCatalogProductIdentity,
  assignMissingProductSkus,
  draftSupplierKey,
  isDraftSupplierKey,
  supplierNameFromDraftKey,
  syncBusinessSequences,
  peekVenteId,
  peekClientId,
  validateFrenchId,
  migrateToFrenchIds
};
